import {useEffect, useState} from 'react'
import {Space, Input} from 'antd'
import {useNavigate} from 'react-router-dom'
import dayjs from 'dayjs'
import styles from './VariationForm.module.scss'
import {useCreateVariationRequest, useUpdateVariationData} from 'modules/request/api.variation'
import {
  Form,
  FormItem,
  InputAmount,
  InputPercent,
  DatePicker, // RangePicker,
  Select,
  Box,
  Button,
  useForm,
  useWatch,
} from 'common/components'
import {formatDate} from 'common/helpers'
import {FIELD_RULES} from 'common/constants'
import { calcMaturityDate } from 'modules/request/components/RequestOpenForm'
import { VARIATION_TYPE_OPTIONS, isSameTerm, isSameAmount, isReduceAmount, isIncreaseAmount, isExtendTerm} from './VariationForm.helper'
import { isMilestonePassed } from 'modules/request'

const {TextArea} = Input

interface VariationFormProps {
  onClose?: () => void
  project: any

  /**
   * when first time initiate a new variation request, this prop should be undefined
   * later when the variation request is ongoing through the review & confirm process, 
   * this prop should have the variation request object
  */ 

  variationRequest?: any
}

const percent0FieldRules = [
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      if (
        (!!value) ||
        (value === 0)
      ) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
  }),
]

const percentFieldRules = [
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      if (value) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
  }),
]
const amountFieldPosRules = [
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      if (
        !!value && (value > 0)
      ) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
  }),
]
const amountFieldNegRules = [
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      if(
        !!value && (value < 0)
      ) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
  }),
]

const amount0FieldRules = [
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      if (value || value === 0) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
  }),
]

const dateFieldRules = (min:string, max?:string) => ([
  ...FIELD_RULES,
  () => ({
    validator(_: any, value: any) {
      // console.log('validator')
      // console.log('value', dayjs(value))
      // console.log('min', dayjs(min))
      // console.log(dayjs(
      //   min
      // ).subtract(
      //   1,'day'
      // ).endOf(
      //   'day'
      // ).isAfter(
      //   dayjs(value), 'day')
      // )

      if (
        (!value) || (!dayjs(value).isValid()) ||
        (!!min) && (dayjs(min).isValid()) && dayjs(min).subtract(1,'day').endOf('day').isAfter(dayjs(value), 'day') ||
        (!!max) && (dayjs(max).isValid()) && dayjs(max).add(1, 'day').startOf('day').isBefore(dayjs(value), 'day')
      ) {
        // console.log('rule reject')
        return Promise.reject()
      }
      // console.log('rule resolve')
      return Promise.resolve()
    },
  }),
])

const isVariationRequestAvailable = (request:any) => (!!request?.uuid)

const AMOUNTLABEL = {
  'INCREASED': 'The amount to be increased',
  'REDUCED': 'The amount to be reduced'
}

export default function VariationForm({
  project,
  variationRequest,
  onClose
}: VariationFormProps) {
  const [amountLabel, setAmountLabel] = useState(AMOUNTLABEL['INCREASED']);
  const [clearMaturityDate, setClearMaturityDate] = useState(false);
  const navigate = useNavigate()
  const {mutate: createRequest, isMutating: isCreating} = useCreateVariationRequest((newReq:any) => {
    onClose?.()
    navigate(`/requests/variation${!!newReq?.uuid ? ('/' + newReq.uuid) : ''}`)
  })
  const {mutate: updateData, isMutating: isUpdating} = useUpdateVariationData(variationRequest?.uuid, onClose)
  const [form] = useForm()
  const variationTypeValue = useWatch('variationType', form)
  const startDateValue = useWatch('startDate', form)

  useEffect(() => {
    // modify amount label
    setAmountLabel(isReduceAmount(variationTypeValue) ? AMOUNTLABEL['REDUCED'] : AMOUNTLABEL['INCREASED']);
    if (isSameTerm(variationTypeValue)) {
      form.setFieldValue('maturityDate', dayjs(project?.maturityDate).toDate())
    } 
    if (isSameAmount(variationTypeValue)) {
      form.setFieldValue('increaseAmount', 0)
    } else if (isReduceAmount(variationTypeValue)) {
      // 保证切换variation type时，increaseAmount置空
      form.setFieldValue('increaseAmount', variationRequest?.increaseAmount ?? '-')
    } else {
      form.setFieldValue('increaseAmount', variationRequest?.increaseAmount)
    }
  }, [variationTypeValue, form, variationRequest, project?.maturityDate])

  const handleFinish = ({ ...values }: any) => {
    // 2023-9-3 copy from postman

    // new
    // "projectUuid": "05173c5a-b2e9-47f9-a0e9-1f70daa3ba6d",
    // "type": "Same term,increase loan amount",
    // "increaseAmount": "10000",
    // "startDate": "2023-10-15",
    // "maturityDate": "2024-12-25",
    // "initialDrawdown": "1000",
    // "interest": "9.99",
    // "establishFee": "1",
    // "lineFee": "2",
    // "legalFeeAmount": "1000",
    // "brokerFeeAmount": "5000",
    // "otherFeeAmount": "200",
    // "comment": "test comment"

    // update
    // "increaseAmount": "10000",
    // "initialDrawdown": "1000",
    // "interest": "9.99",
    // "establishFee": "1.1",
    // "establishFeeAmount": "11",
    // "lineFee": "0.2",
    // "legalFeeAmount": "1001",
    // "brokerFeeAmount": "5001",
    // "otherFeeAmount": "200",
    // "comment": "test comment"

    const newValues:any = {
      startDate       : formatDate(values.startDate, 'server'),
      maturityDate    : formatDate(values.maturityDate, 'server'),
      type            : values.variationType.replace(', ', ','),
      interest        : values.interestRate,

      increaseAmount  : values.increaseAmount,
      initialDrawdown : values.initialDrawdown,
      establishFee    : values.establishFee,
      lineFee         : values.lineFee,
      legalFeeAmount  : values.legalFeeAmount,
      brokerFeeAmount : values.brokerFeeAmount,
      otherFeeAmount  : values.otherFeeAmount,
      comment         : values.comment,
    }

    // return

    if (!isVariationRequestAvailable(variationRequest)) {
      newValues.projectUuid = project?.uuid
      createRequest(newValues)
    } else {
      updateData(newValues)
    }
  }

  const isConfirmedPassed = isMilestonePassed('variation-date-confirmed', variationRequest)
  const reqType = variationRequest?.type  // getType(getTermVariationType(variationRequest?.type), getLoanVariationType(variationRequest?.type))
  // console.log(variationRequest)
  // console.log(reqType)

  const initialValues:any = {
    legalFeeAmount  : (isConfirmedPassed ? variationRequest?.legalFeeAmountConfirmed   : variationRequest?.legalFeeAmount )  ?? 0,
    brokerFeeAmount : (isConfirmedPassed ? variationRequest?.brokerFeeAmountConfirmed  : variationRequest?.brokerFeeAmount)  ?? 0,
    otherFeeAmount  : (isConfirmedPassed ? variationRequest?.otherFeeAmountConfirmed   : variationRequest?.otherFeeAmount )  ?? 0,
    initialDrawdown : (isConfirmedPassed ? variationRequest?.initialDrawdownConfirmed  : variationRequest?.initialDrawdown)  ?? 0,

    increaseAmount  : variationRequest?.increaseAmount,
    interestRate    : variationRequest?.interest ?? 0,
    establishFee    : variationRequest?.establishFee ?? 0,
    lineFee         : variationRequest?.lineFee ?? 0,

    variationType   : reqType || "Extend term, increase loan amount",
    comment         : variationRequest?.commentConfirmed || variationRequest?.comment || '',
  }
  // console.log(variationRequest)
  if (isVariationRequestAvailable(variationRequest)) {
    initialValues.startDate     = dayjs(variationRequest.startDate).toDate();
    initialValues.maturityDate  = dayjs(variationRequest.maturityDate).toDate();
  } else if (process.env.NODE_ENV === 'development') {
    initialValues.comment       = initialValues.comment || 'quick pass comment';
    initialValues.startDate     = dayjs().toDate();
    initialValues.maturityDate  = dayjs(project.maturityDate).add(3, 'month').toDate();

    initialValues.increaseAmount  = 100000;
    initialValues.initialDrawdown = 10000;
    initialValues.legalFeeAmount  = 6700;
    initialValues.brokerFeeAmount = 5400;
    initialValues.otherFeeAmount  = 120;

    initialValues.interestRate    = 7;
    initialValues.establishFee    = 1.1;
    initialValues.lineFee         = 0.2;
  }
    
  if (
    !!variationRequest?.startDate &&
    !!variationRequest?.maturityDate
  ) {
    initialValues.startDate = dayjs().toDate()
    initialValues.maturityDate = calcMaturityDate(
      dayjs(variationRequest.startDate).toDate(),
      dayjs(variationRequest.maturityDate).toDate(),
      initialValues.startDate
    )

    // update by chris @2023-12-14 according to JIRA VCDEC#519
    // initialValues.startDate   = dayjs(variationRequest.startDate).toDate()
    // initialValues.maturityDate= dayjs(variationRequest.maturityDate).toDate()
  }
  useEffect(() => {
    if (isSameTerm(variationTypeValue)) return
    if (clearMaturityDate) {
      form.setFieldValue('maturityDate', '');
      return;
    }
    if (
      !!variationRequest?.startDate &&
      !!variationRequest?.maturityDate
    ) {
      form.setFieldValue('maturityDate', calcMaturityDate(
        dayjs(variationRequest.startDate).toDate(),
        dayjs(variationRequest.maturityDate).toDate(),
        startDateValue
      ))
    }
  }, [startDateValue, clearMaturityDate])

  // const openDate = project?.openDate
  // const maturityDate = project?.maturityDate
  // console.log({openDate, maturityDate})

  // console.log(variationRequest?.startDate)
  const isOverDue = (
    (!!project?.maturityDate) &&
    (dayjs().isAfter(project?.maturityDate, 'day'))
  )
  const startDateMax = !variationRequest?.startDate
    ? (
      isOverDue ? (
        undefined
      ) : (
        dayjs(project?.maturityDate).add(1, 'day').format('YYYY-MM-DD')
      )
    )
    : undefined
  ;
  const startDateMin = !variationRequest?.startDate 
    ? (
      isOverDue ? (
        dayjs(project?.maturityDate).add(1, 'day').format('YYYY-MM-DD')
      ) : (
        dayjs(project?.openDate).format('YYYY-MM-DD')
      )
    ) 
    : (
      project?.openDate || dayjs().format('YYYY-MM-DD')
    )
  ;
  // console.table([{startDateMin, startDateMax, open: project?.openDate, ma: project?.maturityDate}])

  const shouldUpdateMaturityDate = (prevValuse: any, curValues: any) => {
    if (isSameTerm(prevValuse.variationType) && isExtendTerm(curValues.variationType)) {
      setClearMaturityDate(true);
    } else {
      setClearMaturityDate(false);
    }
    return true;
  };

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleFinish}>
      <div className={styles.fieldset}>
        <FormItem
          name="variationType"
          label="Variation type"
          rules={FIELD_RULES}
        >
          <Select options={VARIATION_TYPE_OPTIONS} disabled={isVariationRequestAvailable(variationRequest) && !!reqType} />
        </FormItem>
        <FormItem
          name="increaseAmount"
          label={amountLabel}
          rules={
            isSameAmount(variationTypeValue) ? amount0FieldRules :
            isReduceAmount(variationTypeValue) ? amountFieldNegRules :
            amountFieldPosRules
          }
        >
          <InputAmount 
            disabled={isVariationRequestAvailable(variationRequest) || isSameAmount(variationTypeValue)} 
            min={
              isSameAmount(variationTypeValue) ? 0 :
              isReduceAmount(variationTypeValue) ? undefined :
              1
            }
            max={
              isSameAmount(variationTypeValue) ? 0 :
              isReduceAmount(variationTypeValue) ? -1 :
              undefined
            }
          />
        </FormItem>
      </div>
      <div className={styles.fieldset}>
        <div className={styles['date-fieldset']}>
        <FormItem
          name="startDate"
          label="Variation start date"
          rules={dateFieldRules(startDateMin, startDateMax)}
        >
          <DatePicker 
            disabledDate={(value:Date) => (
              isVariationRequestAvailable(variationRequest) ? 
              (
                dayjs(value).isAfter(dayjs(), 'day')
              )
              :
              (
                dayjs(value).isBefore(dayjs(startDateMin), 'day') ||
                (
                  !!startDateMax &&
                  dayjs(value).isAfter(dayjs(startDateMax), 'day')
                )
              )
            )}
          />
        </FormItem>
        <FormItem
          name="maturityDate"
          label="Variation maturity date"
          rules={
            isSameTerm(variationTypeValue) ? undefined :
            dateFieldRules(project?.maturityDate)
          }
          shouldUpdate={shouldUpdateMaturityDate}
        >
          <DatePicker disabled={
            // !!variationRequest.startDate ||
            isSameTerm(variationTypeValue)
          } minDate={
            dayjs(project?.maturityDate).toDate()
          } />
        </FormItem>
        </div>
        <FormItem
          name="initialDrawdown"
          label="Initial drawdown"
          rules={amount0FieldRules}
        >
          <InputAmount min={0} />
        </FormItem>
      </div>
      <Space size={12} className={styles.values}>
        <FormItem
          name="interestRate"
          label="Interest rate"
          rules={percentFieldRules}
        >
          <InputPercent disabled={isVariationRequestAvailable(variationRequest)} />
        </FormItem>
        <FormItem
          name="establishFee"
          label="Estab. rate"
          rules={percentFieldRules}
        >
          <InputPercent disabled={isVariationRequestAvailable(variationRequest)} />
        </FormItem>
        <FormItem
          name="lineFee"
          label="Line fee rate(M)"
          rules={percent0FieldRules}
        >
          <InputPercent disabled={isVariationRequestAvailable(variationRequest)} />
        </FormItem>
        <FormItem
          name="legalFeeAmount"
          label="Legal Fee"
          rules={amount0FieldRules}
        >
          <InputAmount min={0} />
        </FormItem>
        <FormItem
          name="brokerFeeAmount"
          label="Broker Fee"
          rules={amount0FieldRules}
        >
          <InputAmount min={0} />
        </FormItem>
        <FormItem
          name="otherFeeAmount"
          label="Other Fee"
          rules={amount0FieldRules}
        >
          <InputAmount min={0} />
        </FormItem>
      </Space>
      <FormItem
        name="comment"
        label="Comments"
        rules={FIELD_RULES}
      >
        <TextArea rows={5} />
      </FormItem>
      {/* <Text color="grey" size="xs" align="center" mb={32} mt={-12}>
        * extension fee for variation period from 1 Oct 21 to 20 Feb 22
      </Text> */}
      <Box align="center" mt={12}>
        <Button htmlType="submit" loading={isCreating || isUpdating}>
          {!isVariationRequestAvailable(variationRequest) ? 'Create variation' : 'Confirm'}
        </Button>
      </Box>
    </Form>
  )
}
